/**
 * Global Prism Mode toggle — shared across every tool.
 *
 * When TRUE, every ShootPanel renders the "Search + Lock + Auto-shoot"
 * flow. When FALSE, every ShootPanel renders the classic single-tap
 * reflectorless button.
 *
 * Persists in localStorage so the surveyor doesn't have to re-toggle
 * between sessions. Broadcasts change via the `storage` event AND a
 * custom in-tab event so multiple components on the same page stay in
 * sync without polling.
 */

import { useCallback, useEffect, useState } from "react";

const KEY = "fabaxis.prismMode";
const EVENT = "fabaxis:prism-mode-changed";

const read = () => {
  try { return localStorage.getItem(KEY) === "1"; }
  catch { return false; }
};

export const setPrismModeGlobal = (on) => {
  try {
    localStorage.setItem(KEY, on ? "1" : "0");
    window.dispatchEvent(new CustomEvent(EVENT, { detail: !!on }));
  } catch { /* silent */ }
};

export const usePrismMode = () => {
  const [on, setOn] = useState(read);
  useEffect(() => {
    const onCustom = (e) => setOn(!!e.detail);
    const onStorage = (e) => { if (e.key === KEY) setOn(e.newValue === "1"); };
    window.addEventListener(EVENT, onCustom);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT, onCustom);
      window.removeEventListener("storage", onStorage);
    };
  }, []);
  const setter = useCallback((v) => setPrismModeGlobal(v), []);
  return [on, setter];
};
